import { parseWithZod } from '@conform-to/zod';
import * as E from '@react-email/components';
import { json, type LoaderFunctionArgs, type ActionFunctionArgs, type MetaFunction } from '@remix-run/node';
import { Link, useActionData, useSearchParams } from '@remix-run/react';
import { z } from 'zod';
import { SignupForm, SignupSchema } from '#app/components/auth/SignupForm.tsx';
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx';
import { requireAnonymous } from '#app/utils/auth.server.ts';
import { APP_NAME } from '#app/utils/constants.js';
import { prisma } from '#app/utils/db.server.ts';
import { checkDD214Email } from '#app/utils/dd214-verification.server.ts';
import { checkHoneypot } from '#app/utils/honeypot.server.ts';
import { useIsPending } from '#app/utils/misc.tsx';
import { verifySessionStorage } from '#app/utils/verification.server.ts';
import { handleVerification as handleOnboardingVerification } from './onboarding.server.ts';
import {
  dd214VerificationCannotConfirmSessionKey,
  dd214VerificationEmailSessionKey,
  handleDD214Verification,
} from './verify-dd214.server.ts';

export async function loader({ request }: LoaderFunctionArgs) {
  await requireAnonymous(request);

  return null;
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();

  checkHoneypot(formData);

  const submission = await parseWithZod(formData, {
    schema: SignupSchema.superRefine(async (data, ctx) => {
      const existingUser = await prisma.user.findUnique({
        where: { email: data.email },
        select: { id: true, activatedAt: true },
      });
      if (existingUser && existingUser.activatedAt) {
        ctx.addIssue({
          path: ['email'],
          code: z.ZodIssueCode.custom,
          message: 'A user already exists with this email',
        });
        return;
      }

      if (existingUser && !existingUser.activatedAt) {
        ctx.addIssue({
          path: ['email'],
          code: z.ZodIssueCode.custom,
          message:
            'An account with this email address is pending activation. Please check your email or contact support for assistance.',
        });
        return;
      }
    }),
    async: true,
  });
  if (submission.status !== 'success') {
    return json({ result: submission.reply() }, { status: submission.status === 'error' ? 400 : 200 });
  }
  const { email, firstName, lastName } = submission.value;

  // If this email already got a `cannot_confirm` from /verify-dd214 (order matched, but we
  // couldn't determine veteran status), re-running the CRM check would just redirect back there
  // again — a permanent loop for a customer we already couldn't confirm. Let them through to
  // normal free-member signup instead. A genuine identity mismatch (`no_match`) does NOT set this
  // flag, since that's user-fixable by resubmitting with a corrected name here.
  const verifySession = await verifySessionStorage.getSession(request.headers.get('cookie'));
  const previouslyCannotConfirm =
    verifySession.get(dd214VerificationCannotConfirmSessionKey) === true &&
    verifySession.get(dd214VerificationEmailSessionKey) === email;

  if (!previouslyCannotConfirm) {
    try {
      const dd214Check = await checkDD214Email(email);
      if (dd214Check.status === 'order_found') {
        return handleDD214Verification(email, { firstName, lastName, orderIds: dd214Check.orderIds });
      }
    } catch (error) {
      // A CRM outage/timeout must never block signup for anyone — fail open to normal
      // free-member onboarding, exactly as if no DD214Direct order had been found.
      console.error('checkDD214Email failed during signup; falling through to normal onboarding', error);
    }
  }

  return handleOnboardingVerification(email, { firstName, lastName });
}

export function SignupEmail({ onboardingUrl, otp }: { onboardingUrl: string; otp: string }) {
  return (
    <E.Html lang="en" dir="ltr">
      <E.Container>
        <h1>
          <E.Text>Welcome to {APP_NAME}!</E.Text>
        </h1>
        <p>
          {/* TODO: Tell user to click below to verify email address */}
          <E.Text>
            Here's your verification code: <strong>{otp}</strong>
          </E.Text>
        </p>
        <p>
          <E.Text>Or click the link to get started:</E.Text>
        </p>
        <E.Link href={onboardingUrl}>{onboardingUrl}</E.Link>
      </E.Container>
    </E.Html>
  );
}

export const meta: MetaFunction = () => {
  return [{ title: `Sign Up | ${APP_NAME}` }];
};

export default function SignupRoute() {
  const actionData = useActionData<typeof action>();
  const isPending = useIsPending();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');

  return (
    <div className="container flex flex-col justify-center pb-32 pt-20">
      <div className="text-center">
        <h1 className="text-2xl">Let's start your journey!</h1>
        <p className="mt-4 leading-tight text-muted-foreground">
          Please enter your details and we will send you a verification code.
        </p>
      </div>
      <SignupForm lastResult={actionData?.result} isPending={isPending} redirectTo={redirectTo} />
      <LoginPrompt />
    </div>
  );
}

function LoginPrompt() {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');
  return (
    <div className="flex items-center justify-center gap-2 pt-8">
      <span className="text-muted-foreground">Have an account?</span>
      <Link to={redirectTo ? `/login?${encodeURIComponent(redirectTo)}` : '/login'}>Login</Link>
    </div>
  );
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
